import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Heading_1 as Heading1, PrimaryButton } from '@huggg/react-components';
import dictionary from 'config/dictionary';
import BackButton from '../../BackButton/BackButton';
import styles from '../bankPaymentRedemption.module.scss';

const RECOVERABLE_ERROR_COPY = {
  'name-on-account-mismatch': 'nameMismatch',
  'account-detail-mismatch': 'accountMismatch',
  'missing-bank-details': 'missingDetails',
  'bank-claim-mismatch': 'bankClaimMismatch',
};

const accountKey = (account) => `${account.sortCode}-${account.accountNumber}`;

// UK sortCodes display as XX-XX-XX. The cache may persist them raw (digits
// only, from Token.io) or hyphenated; either way render the user-friendly
// form. Non-digit chars are stripped first so we don't double-hyphenate.
const formatSortCode = (sortCode) => {
  if (!sortCode) return '';
  const digits = sortCode.replace(/\D+/g, '');
  if (digits.length !== 6) return sortCode;
  return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4, 6)}`;
};

const recoverableErrorMessage = (recoverableError) => {
  if (!recoverableError) return null;
  const dictionaryKey = RECOVERABLE_ERROR_COPY[recoverableError.code];
  if (!dictionaryKey) return dictionary.bankPayment.errors.claimFailed;
  return dictionary.bankPayment.errors[dictionaryKey];
};

export const AccountPickerView = ({
  accounts,
  isLoadingAccounts,
  isClaiming,
  recoverableError,
  onSelectAccount,
}) => {
  const [selectedKey, setSelectedKey] = useState(null);

  if (isLoadingAccounts) {
    return (
      <div
        className={styles.container}
        data-test-id="bank-payment-account-loading-view"
      >
        <div className={styles.header}>
          <Heading1>
            {dictionary.bankPayment.labels.accountPickerTitle}
          </Heading1>
        </div>
        <div
          className={styles.spinner}
          aria-label={dictionary.bankPayment.labels.loadingAccountsLabel}
          data-test-id="bank-payment-accounts-spinner"
        />
        <BackButton className={styles.backButton} />
      </div>
    );
  }

  const handleSubmit = () => {
    const selected = accounts.find((a) => accountKey(a) === selectedKey);
    if (selected) onSelectAccount(selected);
  };

  const errorMessage = recoverableErrorMessage(recoverableError);

  return (
    <div
      className={styles.container}
      data-test-id="bank-payment-account-picker-view"
    >
      <div className={styles.header}>
        <Heading1>{dictionary.bankPayment.labels.accountPickerTitle}</Heading1>
      </div>
      <p className={styles.body}>
        {dictionary.bankPayment.copy.accountPickerBody}
      </p>
      <ul className={styles.accountList}>
        {accounts.map((account) => {
          const key = accountKey(account);
          const isSelected = key === selectedKey;
          return (
            <li
              key={key}
              className={`${styles.accountItem} ${
                isSelected ? styles.accountItemSelected : ''
              }`}
              onClick={() => !isClaiming && setSelectedKey(key)}
              data-test-id="bank-payment-account-row"
              data-account-key={key}
            >
              <input
                type="radio"
                name="bank-payment-account"
                className={styles.accountRadio}
                checked={isSelected}
                onChange={() => setSelectedKey(key)}
                disabled={isClaiming}
                aria-label={account.accountHolderName}
              />
              <div className={styles.accountMeta}>
                <span className={styles.accountHolder}>
                  {account.accountHolderName}
                </span>
                <span className={styles.accountNumbers}>
                  {formatSortCode(account.sortCode)} · {account.accountNumber}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
      {errorMessage && (
        <p className={styles.error} data-test-id="bank-payment-account-error">
          {errorMessage}
        </p>
      )}
      <div className={styles.ctaWrapper}>
        <PrimaryButton
          onClick={handleSubmit}
          label={
            isClaiming
              ? dictionary.bankPayment.labels.claimingCta
              : dictionary.bankPayment.labels.selectAccountCta
          }
          disabled={isClaiming || !selectedKey}
          className={styles.cta}
          data-test-id="bank-payment-account-cta"
        />
      </div>
      <BackButton className={styles.backButton} />
    </div>
  );
};

AccountPickerView.propTypes = {
  accounts: PropTypes.arrayOf(
    PropTypes.shape({
      accountHolderName: PropTypes.string.isRequired,
      sortCode: PropTypes.string.isRequired,
      accountNumber: PropTypes.string.isRequired,
    }),
  ),
  isLoadingAccounts: PropTypes.bool,
  isClaiming: PropTypes.bool,
  recoverableError: PropTypes.shape({
    code: PropTypes.string,
    message: PropTypes.string,
  }),
  onSelectAccount: PropTypes.func.isRequired,
};

AccountPickerView.defaultProps = {
  accounts: [],
  isLoadingAccounts: false,
  isClaiming: false,
  recoverableError: null,
};

export default AccountPickerView;
